import type { MetaAdset, MetaCampaign, MetaStructureResponse } from '@nono/contract';
import { Maximize2, Minimize2, Minus, Plus, Scan } from 'lucide-react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { isLive, type Period, resultKind, short, type Verdict, verdict } from './model';

/*
 * The ad account as a canvas you move around in, like a Figma board.
 *
 * Reads left → right: campaign → its ad sets → each ad set's ads fanning out to
 * the right on arcs (biggest spender closest). Campaigns are stacked top →
 * bottom in the order they were made, May first. Size = money, colour = what
 * the money bought, faded = not running.
 *
 * The camera is a plain transform on one <g>: drag or two-finger scroll pans,
 * pinch or ⌘/Ctrl + scroll zooms around the pointer, «Tilpass» frames
 * everything, and the frame can go full screen. Zoom in far enough and the ads
 * get their names, so the board works at every distance.
 */

export type Selection = { kind: 'campaign' | 'adset' | 'ad'; id: string } | null;

const X_CAMP = 70; // campaign, relative to its block
const X_SET = 300; // its ad sets, relative to its block
const TOP_Y = 70;
const BLOCK_GAP = 90; // space between campaign blocks
const ARC = (68 * Math.PI) / 180; // fan spans ±68°
const DOT_GAP = 19; // distance between dot centres on an arc
const RING_GAP = 21;
const SET_GAP = 26;
const MIN_K = 0.06;
const MAX_K = 4;
const LABEL_K = 1.6; // ad names appear from this zoom

const VERDICT_FILL: Record<Verdict, string> = {
  good: 'var(--good)',
  ok: 'var(--fg-muted)',
  bad: 'var(--bad)',
  none: 'transparent',
  young: 'transparent',
};
const VERDICT_STROKE: Record<Verdict, string> = {
  good: 'var(--good)',
  ok: 'var(--fg-muted)',
  bad: 'var(--bad)',
  none: 'var(--bad)',
  young: 'var(--fg-subtle)',
};

interface AdDot {
  id: string;
  x: number;
  y: number;
  r: number;
  v: Verdict;
  live: boolean;
  name: string;
}
interface SetNode {
  s: MetaAdset;
  x: number;
  y: number;
  r: number;
  live: boolean;
  dots: AdDot[];
}
interface CampNode {
  c: MetaCampaign;
  x: number;
  y: number;
  r: number;
  live: boolean;
  sets: SetNode[];
  label: [string, string];
  top: number;
  bottom: number;
  left: number;
  right: number;
  /** Where the time arrow to the next block starts (from the computed layout). */
  arrowX: number;
  arrowY: number;
}
export interface Box {
  x: number;
  y: number;
  r: number;
}

/** "Moss || CBO: 26 Aug | Leads • Instant forms" → ["26 Aug", "Leads • Instant forms"]. */
export function campaignLabel(name: string): [string, string] {
  const clean = name.replace(/^Moss\s*\|\|\s*/i, '').replace(/CBO:\s*/i, '');
  const [a, ...rest] = clean.split('|').map((p) => p.trim());
  return [a ?? clean, rest.join(' · ')];
}

/** Ads on concentric arcs opening to the right; returns positions + how far the fan reaches. */
function fan(n: number, rs: number) {
  const pos: { dx: number; dy: number }[] = [];
  let ring = 0;
  let reach = rs;
  while (pos.length < n) {
    const radius = rs + 24 + ring * RING_GAP;
    const cap = Math.max(1, Math.floor((radius * 2 * ARC) / DOT_GAP) + 1);
    const take = Math.min(cap, n - pos.length);
    for (let j = 0; j < take; j++) {
      // spread this ring's dots evenly over the arc, centred on the horizontal
      const t = take === 1 ? 0 : -ARC + (2 * ARC * j) / (take - 1);
      const a = take < cap ? t * (take / cap) : t;
      pos.push({ dx: Math.cos(a) * radius, dy: Math.sin(a) * radius });
    }
    reach = radius;
    ring++;
  }
  return { pos, halfH: Math.max(rs + 14, reach * Math.sin(ARC) + 10), reach };
}

export type Offsets = Record<string, { dx: number; dy: number }>;
const ZERO = { dx: 0, dy: 0 };

function layout(d: MetaStructureResponse, period: Period, offsets: Offsets) {
  const off = (id: string) => offsets[id] ?? ZERO;
  const fx = d.fxAedToNok;
  const spend = (p: { lifetime: { spend: number }; recent: { spend: number } }) => p[period].spend;
  const maxC = Math.max(1, ...d.campaigns.map(spend));
  const maxS = Math.max(1, ...d.campaigns.flatMap((c) => c.adsets.map(spend)));
  const maxA = Math.max(1, ...d.campaigns.flatMap((c) => c.adsets.flatMap((s) => s.ads.map(spend))));

  const boxes = new Map<string, Box>();
  let ox = 0; // left edge of the current campaign block (as laid out, before any drag)
  const all = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
  const camps: CampNode[] = d.campaigns.map((c) => {
    const xc = ox + X_CAMP;
    const xs = ox + X_SET;
    const co = off(c.id);
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    const grow = (ax: number, ay: number, bx: number, by: number) => {
      x0 = Math.min(x0, ax);
      y0 = Math.min(y0, ay);
      x1 = Math.max(x1, bx);
      y1 = Math.max(y1, by);
    };
    let cursor = TOP_Y;
    let reachMax = 0;
    const sets: SetNode[] = c.adsets.map((s) => {
      const kind = resultKind(s);
      const rs = 7 + 13 * Math.sqrt(spend(s) / maxS);
      const ads = [...s.ads].sort((a, b) => spend(b) - spend(a));
      const f = fan(ads.length, rs);
      const y = cursor + f.halfH;
      cursor = y + f.halfH + SET_GAP;
      reachMax = Math.max(reachMax, f.reach);
      const so = off(s.id);
      const sx = xs + co.dx + so.dx;
      const sy = y + co.dy + so.dy;
      grow(sx - rs - 170, sy - rs - 6, sx + rs, sy + rs + 6); // incl. the label on its left
      const dots: AdDot[] = ads.map((a, i) => {
        const p = f.pos[i]!;
        const ao = off(a.id);
        const dot = {
          id: a.id,
          x: sx + p.dx + ao.dx,
          y: sy + p.dy + ao.dy,
          r: 2.4 + 6.5 * Math.sqrt(spend(a) / maxA),
          v: verdict(a[period], kind, fx),
          live: isLive(a) && isLive(s) && isLive(c),
          name: a.name,
        };
        boxes.set(a.id, { x: dot.x, y: dot.y, r: dot.r + 40 });
        grow(dot.x - dot.r, dot.y - dot.r, dot.x + dot.r + 12, dot.y + dot.r);
        return dot;
      });
      boxes.set(s.id, { x: sx + f.reach / 2, y: sy, r: Math.max(f.reach / 2 + 30, f.halfH) });
      return { s, x: sx, y: sy, r: rs, live: isLive(s) && isLive(c), dots };
    });
    const baseBottom = Math.max(cursor - SET_GAP, TOP_Y + 80);
    // the campaign sits between its FIRST and LAST ad set as laid out (not as dragged)
    const baseY = c.adsets.length ? (TOP_Y + baseBottom) / 2 : TOP_Y + 40;
    const r = 10 + 22 * Math.sqrt(spend(c) / maxC);
    const cx = xc + co.dx;
    const cy = baseY + co.dy;
    grow(cx - Math.max(r, 90), cy - r - 40, cx + r, cy + r);
    if (!Number.isFinite(x0)) grow(ox, TOP_Y, xs + 60, TOP_Y + 80);
    const pad = 30;
    const left = x0 - pad;
    const right = x1 + pad;
    const top = y0 - pad;
    const bottom = y1 + pad;
    boxes.set(c.id, { x: (left + right) / 2, y: (top + bottom) / 2, r: Math.max((right - left) / 2, (bottom - top) / 2) });
    all.x0 = Math.min(all.x0, left);
    all.y0 = Math.min(all.y0, top);
    all.x1 = Math.max(all.x1, right);
    all.y1 = Math.max(all.y1, bottom);
    const baseRight = xs + reachMax + 30;
    const node: CampNode = {
      c,
      x: cx,
      y: cy,
      r,
      live: isLive(c),
      sets,
      label: campaignLabel(c.name),
      top,
      bottom,
      left,
      right,
      arrowX: baseRight,
      arrowY: TOP_Y - 30,
    };
    ox = baseRight + BLOCK_GAP;
    return node;
  });

  return { camps, boxes, bounds: all };
}

/* ── Camera ──────────────────────────────────────────────────────────────── */

interface Cam {
  x: number;
  y: number;
  k: number;
}
const clampK = (k: number) => Math.min(MAX_K, Math.max(MIN_K, k));

function fitCam(
  vw: number,
  vh: number,
  b: { x0: number; y0: number; x1: number; y1: number },
  pad = 40,
): Cam {
  const k = clampK(Math.min((vw - pad * 2) / (b.x1 - b.x0), (vh - pad * 2) / (b.y1 - b.y0), 1.6));
  return { k, x: vw / 2 - ((b.x0 + b.x1) / 2) * k, y: vh / 2 - ((b.y0 + b.y1) / 2) * k };
}

export function AccountCanvas({
  data,
  period,
  onlyLive,
  selected,
  highlight,
  onSelect,
  offsets,
  onMove,
  flyTo,
  toolbar,
  overlay,
  legend,
}: {
  data: MetaStructureResponse;
  period: Period;
  onlyLive: boolean;
  selected: Selection;
  highlight: Set<string>;
  onSelect: (s: Selection) => void;
  /** Saved drag offsets (shared layout). */
  offsets: Offsets;
  /** Called once when a drag ends, with the new offset of what was moved. */
  onMove: (moved: { id: string; dx: number; dy: number }) => void;
  /** Entity ids to frame; a new array instance flies the camera there. */
  flyTo: string[] | null;
  toolbar?: ReactNode;
  overlay?: ReactNode;
  legend?: ReactNode;
}) {
  // a drag in progress is drawn live; it is committed (onMove) when released
  const [live, setLive] = useState<{ id: string; dx: number; dy: number } | null>(null);
  const merged = useMemo(() => (live ? { ...offsets, [live.id]: { dx: live.dx, dy: live.dy } } : offsets), [offsets, live]);
  const { camps, boxes, bounds } = useMemo(() => layout(data, period, merged), [data, period, merged]);
  const frameRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [cam, setCam] = useState<Cam>({ x: 0, y: 0, k: 1 });
  const [anim, setAnim] = useState(false);
  const [full, setFull] = useState(false);
  const fitted = useRef(false);

  // viewport size — measured right away and on window resize as well as by the
  // observer: ResizeObserver only reports on a painted frame, and a background
  // or throttled tab may not paint, which left the board unfitted at 100 %.
  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize((p) => (p.w === r.width && p.h === r.height ? p : { w: r.width, h: r.height }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [full]);

  const fly = useCallback((next: Cam) => {
    setAnim(true);
    setCam(next);
    window.setTimeout(() => setAnim(false), 480);
  }, []);

  const fitAll = useCallback(
    (animate = true) => {
      if (!size.w) return;
      const next = fitCam(size.w, size.h, bounds);
      animate ? fly(next) : setCam(next);
    },
    [size, bounds, fly],
  );

  // first frame: fit everything
  useEffect(() => {
    if (size.w && !fitted.current) {
      fitted.current = true;
      fitAll(false);
    }
  }, [size, fitAll]);

  // re-fit when going in or out of full screen (after the new frame is measured)
  const fitRef = useRef(fitAll);
  fitRef.current = fitAll;
  const firstFull = useRef(true);
  useEffect(() => {
    if (firstFull.current) {
      firstFull.current = false;
      return;
    }
    const t = window.setTimeout(() => fitRef.current(true), 60);
    return () => window.clearTimeout(t);
  }, [full]);

  // fly to a set of entities
  useEffect(() => {
    if (!flyTo?.length || !size.w) return;
    const bs = flyTo.map((id) => boxes.get(id)).filter((b): b is Box => !!b);
    if (!bs.length) return;
    const b = {
      x0: Math.min(...bs.map((q) => q.x - q.r)),
      y0: Math.min(...bs.map((q) => q.y - q.r)),
      x1: Math.max(...bs.map((q) => q.x + q.r)),
      y1: Math.max(...bs.map((q) => q.y + q.r)),
    };
    fly(fitCam(size.w, size.h, b, 60));
  }, [flyTo, boxes, size, fly]);

  // wheel: pan, or zoom with ⌘/Ctrl (trackpad pinch arrives as ctrl+wheel)
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      setCam((c) => {
        if (e.ctrlKey || e.metaKey) {
          const k = clampK(c.k * Math.exp(-e.deltaY * 0.01));
          return { k, x: px - ((px - c.x) / c.k) * k, y: py - ((py - c.y) / c.k) * k };
        }
        return { ...c, x: c.x - e.deltaX, y: c.y - e.deltaY };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [full]);

  // drag to pan, two fingers to pinch
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const moved = useRef(false);
  const start = useRef({ x: 0, y: 0 });
  const pinch = useRef<{ d: number; k: number; cx: number; cy: number; cam: Cam } | null>(null);
  const [grabbing, setGrabbing] = useState(false);

  const nodeDrag = useRef<{ id: string; base: { dx: number; dy: number } } | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    start.current = { x: e.clientX, y: e.clientY };
    moved.current = false;
    // grabbing a campaign, ad set or ad moves IT; grabbing the board pans
    const hit = (e.target as Element).closest('[data-node-id]');
    const id = hit?.getAttribute('data-node-id');
    nodeDrag.current = id && pointers.current.size === 1 ? { id, base: offsets[id] ?? ZERO } : null;
    if (pointers.current.size === 2) nodeDrag.current = null;
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()] as [{ x: number; y: number }, { x: number; y: number }];
      const rect = svgRef.current!.getBoundingClientRect();
      pinch.current = {
        d: Math.hypot(a.x - b.x, a.y - b.y),
        k: cam.k,
        cx: (a.x + b.x) / 2 - rect.left,
        cy: (a.y + b.y) / 2 - rect.top,
        cam,
      };
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    const dx = e.clientX - prev.x;
    const dy = e.clientY - prev.y;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (Math.abs(dx) + Math.abs(dy) > 0) {
      if (!moved.current && Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) < 4) return;
      if (!moved.current) {
        // capture only once it is a real drag, so plain clicks still reach the dots
        svgRef.current?.setPointerCapture(e.pointerId);
        moved.current = true;
        setGrabbing(true);
      }
    }
    if (nodeDrag.current && pointers.current.size === 1) {
      const n = nodeDrag.current;
      setLive({
        id: n.id,
        dx: n.base.dx + (e.clientX - start.current.x) / cam.k,
        dy: n.base.dy + (e.clientY - start.current.y) / cam.k,
      });
      return;
    }
    if (pinch.current && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()] as [{ x: number; y: number }, { x: number; y: number }];
      const p = pinch.current;
      const k = clampK(p.k * (Math.hypot(a.x - b.x, a.y - b.y) / p.d));
      setCam({ k, x: p.cx - ((p.cx - p.cam.x) / p.cam.k) * k, y: p.cy - ((p.cy - p.cam.y) / p.cam.k) * k });
      return;
    }
    setCam((c) => ({ ...c, x: c.x + dx, y: c.y + dy }));
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (nodeDrag.current && moved.current && live) onMove(live);
    nodeDrag.current = null;
    setLive(null);
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (!pointers.current.size) setGrabbing(false);
  };
  /** Clicks only count when the pointer didn't travel (otherwise it was a pan). */
  const pick = (s: Selection) => () => {
    if (!moved.current) onSelect(s);
  };

  const zoomBy = (f: number) =>
    fly(
      ((c) => {
        const k = clampK(c.k * f);
        const px = size.w / 2;
        const py = size.h / 2;
        return { k, x: px - ((px - c.x) / c.k) * k, y: py - ((py - c.y) / c.k) * k };
      })(cam),
    );

  // Full screen: the board leaves the card and covers the whole page (a portal,
  // so no ancestor's overflow/transform can trap it), and the browser goes
  // native full screen where it allows it (not iOS Safari — the overlay still works).
  const wrapRef = useRef<HTMLDivElement>(null);
  const toggleFull = () => {
    if (full) {
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
      setFull(false);
    } else {
      setFull(true);
      document.documentElement.requestFullscreen?.().catch(() => undefined);
    }
  };
  useEffect(() => {
    const onFs = () => {
      if (!document.fullscreenElement) setFull(false);
    };
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);
  useEffect(() => {
    if (!full) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [full]);

  // keyboard, while the board has focus or is full screen
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && /input|textarea|select/i.test(t.tagName)) return;
      if (!full && !wrapRef.current?.contains(document.activeElement)) return;
      if (e.key === '+' || e.key === '=') zoomBy(1.3);
      else if (e.key === '-') zoomBy(1 / 1.3);
      else if (e.key === '0') fitAll();
      else if (e.key === 'f' || e.key === 'F') toggleFull();
      else if (e.key === 'Escape' && full && !document.fullscreenElement) setFull(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const sel = selected?.id;
  const dim = (live: boolean) => (onlyLive && !live ? 0.07 : live ? 1 : 0.55);
  const showAdNames = cam.k >= LABEL_K;
  // keep text a readable size on screen whatever the zoom
  const fs = (px: number) => px / Math.max(0.8, Math.min(cam.k, 1.4));

  const board = (
    <div
      ref={wrapRef}
      tabIndex={-1}
      className={cn(
        'relative flex flex-col overflow-hidden bg-surface outline-none',
        full ? 'fixed inset-0 z-[60] h-[100dvh] w-screen' : 'h-[72vh] min-h-[480px]',
      )}
    >
      {/* page controls, top left */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 p-2 sm:p-3">
        <div className="pointer-events-auto flex max-w-full flex-wrap items-center gap-2">{toolbar}</div>
      </div>

      <div ref={frameRef} className="relative min-h-0 flex-1 overflow-hidden">
        {/* dotted board */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage: 'radial-gradient(var(--border-strong) 1px, transparent 1px)',
            backgroundSize: `${22 * cam.k}px ${22 * cam.k}px`,
            backgroundPosition: `${cam.x}px ${cam.y}px`,
          }}
        />
        <svg
          ref={svgRef}
          className={cn('absolute inset-0 h-full w-full touch-none select-none', grabbing ? 'cursor-grabbing' : 'cursor-grab')}
          role="img"
          aria-label="Meta-kontoen som et lerret: kampanjer, annonsesett og annonser fra venstre mot høyre"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onClick={(e) => {
            if (e.target === e.currentTarget && !moved.current) onSelect(null);
          }}
        >
          <defs>
            <marker id="meta-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
              <path d="M0 0 L8 4 L0 8 z" fill="var(--border-strong)" />
            </marker>
            <radialGradient id="meta-glow">
              <stop offset="0%" stopColor="var(--brand)" stopOpacity="0.35" />
              <stop offset="100%" stopColor="var(--brand)" stopOpacity="0" />
            </radialGradient>
          </defs>
          <g
            transform={`translate(${cam.x} ${cam.y}) scale(${cam.k})`}
            style={{ transition: anim ? 'transform 460ms cubic-bezier(.2,.7,.2,1)' : undefined }}
          >
            {camps.map((cn_, ci) => (
              <g key={cn_.c.id}>
                {/* block band — one campaign, read left to right */}
                <rect
                  x={cn_.left}
                  y={cn_.top}
                  width={cn_.right - cn_.left}
                  height={cn_.bottom - cn_.top}
                  rx={28}
                  fill={ci % 2 ? 'transparent' : 'var(--surface-2)'}
                  stroke="var(--border)"
                  strokeOpacity={0.6}
                  opacity={0.55}
                />
                {ci < camps.length - 1 && (
                  <path
                    d={`M ${cn_.arrowX + 14} ${cn_.arrowY} L ${cn_.arrowX + BLOCK_GAP - 14} ${cn_.arrowY}`}
                    stroke="var(--border-strong)"
                    strokeDasharray="3 5"
                    markerEnd="url(#meta-arrow)"
                  />
                )}
                {/* campaign → ad set curves */}
                {cn_.sets.map((sn) => (
                  <path
                    key={`e-${sn.s.id}`}
                    d={`M ${cn_.x + cn_.r} ${cn_.y} C ${(cn_.x + sn.x) / 2} ${cn_.y}, ${(cn_.x + sn.x) / 2} ${sn.y}, ${sn.x - sn.r} ${sn.y}`}
                    fill="none"
                    stroke="var(--border-strong)"
                    strokeWidth={1.3}
                    opacity={dim(sn.live)}
                  />
                ))}

                {/* campaign */}
                <g
                  opacity={dim(cn_.live)}
                  data-node-id={cn_.c.id}
                  className={live?.id === cn_.c.id ? 'cursor-grabbing' : 'cursor-pointer'}
                  onClick={pick({ kind: 'campaign', id: cn_.c.id })}
                >
                  {cn_.live && <circle cx={cn_.x} cy={cn_.y} r={cn_.r * 2.4} fill="url(#meta-glow)" />}
                  <circle
                    cx={cn_.x}
                    cy={cn_.y}
                    r={cn_.r}
                    fill={cn_.live ? 'var(--brand)' : 'var(--surface-3)'}
                    stroke={sel === cn_.c.id ? 'var(--fg)' : highlight.has(cn_.c.id) ? 'var(--warn)' : 'var(--border-strong)'}
                    strokeWidth={sel === cn_.c.id || highlight.has(cn_.c.id) ? 3 : 1}
                  />
                  <text x={cn_.x} y={cn_.y - cn_.r - 10 - fs(12)} textAnchor="middle" className="fill-[var(--fg)] font-semibold" fontSize={fs(14)}>
                    {cn_.label[0]}
                  </text>
                  <text x={cn_.x} y={cn_.y - cn_.r - 8} textAnchor="middle" className="fill-[var(--fg-subtle)]" fontSize={fs(11)}>
                    {cn_.label[1].length > 30 ? `${cn_.label[1].slice(0, 29)}…` : cn_.label[1]}
                  </text>
                </g>

                {cn_.sets.map((sn) => (
                  <g key={sn.s.id} opacity={dim(sn.live)}>
                    {sn.dots.map((d) => (
                      <line key={`t-${d.id}`} x1={sn.x} y1={sn.y} x2={d.x} y2={d.y} stroke="var(--border)" strokeWidth={0.6} />
                    ))}
                    <circle
                      cx={sn.x}
                      cy={sn.y}
                      r={sn.r}
                      className="cursor-pointer"
                      fill={sn.live ? 'var(--surface)' : 'var(--surface-2)'}
                      stroke={
                        sel === sn.s.id
                          ? 'var(--fg)'
                          : highlight.has(sn.s.id)
                            ? 'var(--warn)'
                            : resultKind(sn.s) === 'booking'
                              ? 'var(--accent-booking)'
                              : 'var(--accent-lead)'
                      }
                      strokeWidth={sel === sn.s.id || highlight.has(sn.s.id) ? 3.5 : 2}
                      data-node-id={sn.s.id}
                      onClick={pick({ kind: 'adset', id: sn.s.id })}
                    />
                    <text
                      x={sn.x - sn.r - 8}
                      y={sn.y + fs(4)}
                      textAnchor="end"
                      className={sn.live ? 'fill-[var(--fg-muted)]' : 'fill-[var(--fg-subtle)]'}
                      fontSize={fs(11.5)}
                    >
                      {(() => {
                        const t = short(sn.s.name);
                        return t.length > 26 ? `${t.slice(0, 25)}…` : t;
                      })()}
                    </text>
                    {sn.dots.map((d) => {
                      const hi = highlight.has(d.id);
                      const on = sel === d.id;
                      return (
                        <g key={d.id} data-node-id={d.id} className="cursor-pointer" onClick={pick({ kind: 'ad', id: d.id })}>
                          <title>{d.name}</title>
                          {(hi || on) && (
                            <circle cx={d.x} cy={d.y} r={d.r + 3.5} fill="none" stroke={on ? 'var(--fg)' : 'var(--warn)'} strokeWidth={1.6} />
                          )}
                          <circle
                            cx={d.x}
                            cy={d.y}
                            r={d.r}
                            fill={VERDICT_FILL[d.v]}
                            fillOpacity={d.live ? 0.95 : 0.6}
                            stroke={VERDICT_STROKE[d.v]}
                            strokeWidth={d.v === 'none' || d.v === 'young' ? 1.3 : 0}
                          />
                          <circle cx={d.x} cy={d.y} r={Math.max(d.r, 7)} fill="transparent" />
                          {showAdNames && (
                            <text x={d.x + d.r + 3} y={d.y + 2.5} className="pointer-events-none fill-[var(--fg-muted)]" fontSize={7}>
                              {adLabel(d.name)}
                            </text>
                          )}
                        </g>
                      );
                    })}
                  </g>
                ))}
              </g>
            ))}
          </g>
        </svg>
        {overlay}
      </div>

      {/* legend bottom left, camera bottom right — like a design tool */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-end justify-between gap-2 p-2 sm:p-3">
        <div className="pointer-events-auto min-w-0">{legend}</div>
        <div className="pointer-events-auto flex shrink-0 items-center gap-1 rounded-full border border-border bg-surface/90 p-0.5 shadow-card backdrop-blur">
          <CamBtn label="Zoom ut (−)" onClick={() => zoomBy(1 / 1.3)}>
            <Minus className="size-4" />
          </CamBtn>
          <span className="tabular w-11 text-center text-micro text-fg-muted">{Math.round(cam.k * 100)} %</span>
          <CamBtn label="Zoom inn (+)" onClick={() => zoomBy(1.3)}>
            <Plus className="size-4" />
          </CamBtn>
          <CamBtn label="Tilpass alt (0)" onClick={() => fitAll()}>
            <Scan className="size-4" />
          </CamBtn>
          <CamBtn label={full ? 'Avslutt fullskjerm (F)' : 'Fullskjerm (F)'} onClick={toggleFull}>
            {full ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
          </CamBtn>
        </div>
      </div>
    </div>
  );

  if (!full) return board;
  return (
    <>
      {/* keep the card's space while the board is out in full screen */}
      <div className="h-[72vh] min-h-[480px]" />
      {createPortal(board, document.body)}
    </>
  );
}

/** "Moss || 27 Aug • Elias • AI - Effektiviteten" → "AI - Effektiviteten". */
function adLabel(name: string): string {
  const t = name.split(/[•|]+/).map((p) => p.trim()).filter(Boolean).slice(-1)[0] ?? name;
  return t.length > 28 ? `${t.slice(0, 27)}…` : t;
}

function CamBtn({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="flex size-9 items-center justify-center rounded-full text-fg-muted transition-fast hover:bg-surface-3 hover:text-fg sm:size-8"
    >
      {children}
    </button>
  );
}
